<template>
    <section class="member-admin">
        <div class="member-panel">
            <div class="member-heading"><h2>회원 관리</h2><button type="button" @click="downloadExcel" :disabled="!ready || !filtered.length">엑셀 다운로드</button></div>
            <p class="member-hint">이름을 누르면 상세 정보를 볼 수 있습니다. 모든 회원은 연회원으로 관리합니다.</p>
            <div class="member-stats"><span>전체 <strong>{{ members.length }}명</strong></span><span>입금 완료 <strong>{{ paidCount }}명</strong></span><span>미입금 <strong>{{ members.length - paidCount }}명</strong></span></div>
            <div class="bulk-payment">
                <button type="button" @click="markAllUnpaid" :disabled="!ready || !members.length || !!busyKey">{{ busyKey === '__all__' ? '전체 처리 중…' : '전체 미입금 처리' }}</button>
                <p class="member-hint">검색·필터와 관계없이 전체 회원의 회비 상태를 미입금으로 변경합니다. ‘초기화’를 입력해야 실행됩니다.</p>
            </div>
            <label for="member-search">회원 검색</label><input id="member-search" v-model="search" type="search" placeholder="이름, 아이디, 전화번호 검색">
            <div class="member-filters"><div><label for="member-payment">입금 상태</label><select id="member-payment" v-model="payment"><option value="">전체 상태</option><option value="paid">입금 완료</option><option value="unpaid">미입금</option></select></div></div>
        </div>
        <p class="member-message" role="status">{{ message }}</p>
        <p class="member-hint" v-if="ready">조회 {{ filtered.length }}명</p>
        <p class="member-legend"><span class="legend-paid">파란색 · 입금 완료</span><span class="legend-unpaid">분홍색 · 미입금</span></p>
        <div class="member-grid">
            <details v-for="member in filtered" :key="member.key" class="member-card" :class="isPaid(member) ? 'card-paid' : 'card-unpaid'">
                <summary>
                    <span class="member-name" :title="member.userName || '이름 없음'">{{ member.userName || '이름 없음' }}</span>
                    <span class="member-badge">연회원</span>
                    <span class="member-status">{{ isPaid(member) ? '입금 완료' : '미입금' }}</span>
                    <span aria-hidden="true" class="expand-arrow">⌄</span>
                </summary>
                <div class="member-detail">
                    <dl><div><dt>아이디</dt><dd>{{ member.userId || '—' }}</dd></div><div><dt>가입일</dt><dd>{{ member.regDt || '—' }}</dd></div><div><dt>생년월일 / 성별</dt><dd>{{ member.age || '—' }} / {{ member.sex || '—' }}</dd></div><div><dt>전화번호</dt><dd>{{ member.phone || '—' }}</dd></div></dl>
                    <div class="member-actions"><button type="button" :class="{ paid: isPaid(member) }" @click="togglePayment(member)" :disabled="!!busyKey || !ready">{{ busyKey === member.key ? '처리 중…' : isPaid(member) ? '미입금으로 변경' : '입금 완료로 변경' }}</button><button type="button" class="member-delete" @click="deleteMember(member)" :disabled="!!busyKey || !ready">회원 삭제</button></div>
                </div>
            </details>
        </div>
        <p v-if="ready && !filtered.length" class="member-hint">{{ members.length ? '검색 조건에 맞는 회원이 없습니다.' : '등록된 회원이 없습니다.' }}</p>
    </section>
</template>

<script>
import { getDatabase, ref, onValue, remove, runTransaction } from 'firebase/database';
import * as XLSX from 'xlsx-js-style';
import { isMemberPaid, markMembersUnpaid } from '@/utils/member-payment';

export default {
    data() { return { members: [], search: '', payment: '', ready: false, message: '회원 명단을 불러오는 중…', busyKey: '', unsubscribe: null }; },
    computed: {
        paidCount() { return this.members.filter(member => this.isPaid(member)).length; },
        filtered() {
            const query = this.search.trim().toLowerCase();
            const digits = query.replace(/\D/g, '');
            return this.members.filter(member => {
                const text = [member.userName, member.userId, member.phone].filter(Boolean).join(' ').toLowerCase();
                const matchesSearch = !query || text.includes(query) || (digits && /^[\d\s()+-]+$/.test(query) && String(member.phone || '').replace(/\D/g, '').includes(digits));
                return matchesSearch && (!this.payment || (this.payment === 'paid' ? this.isPaid(member) : !this.isPaid(member)));
            });
        }
    },
    mounted() {
        this.unsubscribe = onValue(ref(getDatabase(), 'signUp'), snapshot => {
            this.members = Object.entries(snapshot.val() || {}).filter(([key, value]) => key !== 'signUp_exemptCount' && value && typeof value === 'object').map(([key, value]) => ({ ...value, membershipType: '연회원', key })).reverse();
            this.ready = true;
            if (!this.busyKey) this.message = '';
        }, () => { this.ready = false; this.message = '회원 명단을 불러오지 못했습니다. 연결 상태와 접근 권한을 확인해 주세요.'; });
    },
    beforeDestroy() { if (this.unsubscribe) this.unsubscribe(); },
    methods: {
        async markAllUnpaid() {
            if (!this.ready || !this.members.length || this.busyKey) return;
            const confirmation = window.prompt('검색·필터와 관계없이 전체 회원 ' + this.members.length + '명의 회비 상태를 미입금으로 변경합니다. 회원 정보는 유지됩니다.\n\n실행하려면 아래에 ‘초기화’를 정확히 입력해 주세요.');
            if (confirmation === null) return;
            if (confirmation !== '초기화') {
                this.message = '‘초기화’가 정확히 입력되지 않아 취소했습니다. 회원 상태는 변경하지 않았습니다.';
                return;
            }
            this.busyKey = '__all__';
            this.message = '전체 회원을 미입금으로 변경하는 중…';
            try {
                const result = await runTransaction(ref(getDatabase(), 'signUp'), markMembersUnpaid, { applyLocally: false });
                this.message = result.committed ? '전체 회원의 회비 상태를 미입금으로 변경했습니다.' : '처리할 회원 정보가 없습니다. 명단을 확인해 주세요.';
            } catch (error) { this.message = '전체 미입금 처리에 실패했습니다. 연결 상태와 접근 권한을 확인한 뒤 다시 시도해 주세요.'; }
            finally { this.busyKey = ''; }
        },
        isPaid(member) { return isMemberPaid(member); },
        async togglePayment(member) {
            if (this.busyKey || !this.ready) return;
            this.busyKey = member.key;
            try {
                const result = await runTransaction(ref(getDatabase(), 'signUp/' + member.key), current => {
                    if (!current) return;
                    return { ...current, membershipType: '연회원', paid: !isMemberPaid(current) };
                }, { applyLocally: false });
                this.message = result.committed ? '회비 입금 상태를 변경했습니다.' : '회원 정보가 변경되었습니다. 명단을 다시 확인해 주세요.';
            } catch (error) { this.message = '입금 상태를 변경하지 못했습니다. 다시 시도해 주세요.'; }
            finally { this.busyKey = ''; }
        },
        async deleteMember(member) {
            if (this.busyKey || !this.ready || !confirm((member.userName || '이 회원') + '님의 회원가입 정보를 삭제할까요? 복구할 수 없습니다. 훈련 신청과 마일리지 기록은 유지됩니다.')) return;
            this.busyKey = member.key;
            try {
                await remove(ref(getDatabase(), 'signUp/' + member.key));
                this.message = '회원가입 정보를 삭제했습니다.';
            } catch (error) { this.message = '삭제하지 못했습니다. 다시 시도해 주세요.'; }
            finally { this.busyKey = ''; }
        },
        downloadExcel() {
            if (!this.ready || !this.filtered.length) return;
            const rows = this.filtered.map((member, index) => ({ 번호: index + 1, 가입일: member.regDt || '', 아이디: member.userId || '', 이름: member.userName || '', 성별: member.sex || '', 생년월일: member.age || '', 전화번호: member.phone || '', 회원유형: '연회원', 회비: this.isPaid(member) ? '입금 완료' : '미입금' }));
            const sheet = XLSX.utils.json_to_sheet(rows);
            sheet['!cols'] = [6, 23, 18, 15, 8, 14, 20, 12, 12].map(wch => ({ wch }));
            const book = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(book, sheet, '회원명단');
            XLSX.writeFile(book, 'ZONE5_회원명단.xlsx');
        }
    }
};
</script>

<style scoped>
.member-admin { min-width:0; }
.member-panel { border:1px solid #e2e8f0; border-radius:16px; background:#fff; padding:18px; margin-bottom:16px; }
.member-heading { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
h2 { font-size:17px; font-weight:800; margin:0; } h3 { font-size:16px; font-weight:800; margin:0; overflow-wrap:anywhere; }
.member-hint { font-size:14px; color:#475569; line-height:1.7; margin:12px 0; }
.member-stats { display:flex; flex-wrap:wrap; gap:16px; padding:16px 0; margin-bottom:12px; border-bottom:1px solid #edf1f7; font-size:14px; color:#475569; }
.member-stats strong { color:#2563eb; margin-left:4px; }
.bulk-payment { margin:0 0 20px; }
.bulk-payment button { color:#be123c; background:#fff1f2; border-color:#fecdd3; }
.bulk-payment .member-hint { margin:8px 0 0; }
label { display:block; margin-bottom:7px; font-size:14px; font-weight:700; color:#475569; }
input, select { width:100%; box-sizing:border-box; min-height:44px; border:1px solid #dbe3ef; border-radius:10px; background:#fff; color:#172033; font-size:15px; padding:10px 12px; }
.member-filters { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; margin-top:14px; }
input:focus, select:focus { outline:2px solid #bfdbfe; border-color:#60a5fa; }
button { cursor:pointer; min-height:40px; padding:8px 12px; border:1px solid #dbe3ef; border-radius:9px; background:#f8fafc; color:#475569; font-size:14px; }
button:disabled { opacity:.5; cursor:default; } button:focus-visible { outline:3px solid #93c5fd; outline-offset:3px; }
.member-heading button { color:#2563eb; background:#eff6ff; }
.member-badge { font-size:14px; color:#2563eb; background:#eff6ff; padding:4px 8px; border-radius:16px; }
dl { margin:16px 0; } dl > div { display:flex; gap:12px; margin:8px 0; font-size:14px; line-height:1.6; } dt { flex:0 0 100px; color:#475569; font-weight:400; } dd { margin:0; overflow-wrap:anywhere; min-width:0; }
.member-actions { display:flex; gap:8px; justify-content:space-between; border-top:1px solid #edf1f7; padding-top:12px; }
.member-actions button { color:#b45309; background:#fffbeb; border-color:#fde68a; } .member-actions button.paid { color:#1d4ed8; background:#eff6ff; border-color:#bfdbfe; }
.member-actions .member-delete { color:#be123c; background:#fff; border-color:#fecdd3; }
.member-message { color:#1d4ed8; font-size:14px; line-height:1.7; }

.member-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; align-items:start; }
.member-card { min-width:0; border:1px solid; border-radius:14px; overflow:hidden; }
.card-paid { background:#eff6ff; border-color:#bfdbfe; }
.card-unpaid { background:#fff1f2; border-color:#fecdd3; }
.member-card summary { display:flex; align-items:center; flex-wrap:nowrap; gap:6px; min-height:48px; box-sizing:border-box; padding:10px; cursor:pointer; list-style:none; }
.member-card summary::-webkit-details-marker { display:none; }
.member-name { flex:1; min-width:0; font-size:15px; line-height:1.5; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.member-card .member-badge { flex-shrink:0; padding:3px 5px; white-space:nowrap; background:#ffffffb3; color:#475569; }
.member-status { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
.expand-arrow { flex-shrink:0; font-size:16px; transition:transform .15s; }
.member-card[open] .expand-arrow { transform:rotate(180deg); }
.member-month { font-size:14px; line-height:1.5; color:#475569; overflow-wrap:anywhere; }
.member-detail { padding:0 12px 14px; border-top:1px solid #ffffff; }
.member-detail dl > div { display:block; margin:12px 0; }
.member-detail dt { font-size:14px; margin-bottom:3px; }
.member-detail dd { font-size:14px; }
.member-detail .member-actions { flex-direction:column; gap:6px; }
.month-setting input { padding:8px 4px; font-size:14px; min-width:0; max-width:100%; }
.month-setting .member-hint { font-size:14px; }
.member-legend { display:flex; flex-wrap:wrap; gap:12px; margin:12px 0; font-size:14px; }
.legend-paid { color:#1d4ed8; } .legend-unpaid { color:#be123c; }
.member-card summary:focus-visible { outline:3px solid #60a5fa; outline-offset:-3px; }
</style>
